import { useApi } from './useApi';
import { useModeAwareQuery } from './useModeAwareQuery';
import { getGlobalDataStore } from '@/lib/mock-data/global-data-store';
import {
  mapPromiseFromApi,
  type PromiseToPayListItemApi,
  type PaginationApi,
} from './usePromisesToPayData';
import type { PromiseToPay } from '@/types/collections';

export interface GetCustomerPromisesParams extends Record<string, string | number | boolean | undefined> {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

interface GetCustomerPromisesApiResponse {
  data: PromiseToPayListItemApi[];
  pagination: PaginationApi;
}

interface CustomerPromisesResult {
  promises: PromiseToPay[];
  pagination: PaginationApi;
}

const defaultPagination: PaginationApi = {
  page: 1,
  pageSize: 20,
  totalItems: 0,
  totalPages: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

/**
 * Hook to fetch promises for a specific customer.
 * Mode-aware: uses real API in Live mode, mock data in Demo mode.
 * Supports server-side pagination, sorting, and filtering.
 */
export function useCustomerPromises(
  customerId: string | undefined,
  params?: GetCustomerPromisesParams,
) {
  const { apiGet } = useApi();

  const queryParams: GetCustomerPromisesParams = {
    page: 1,
    pageSize: 20,
    sortBy: 'PromiseDate',
    sortOrder: 'desc',
    ...params,
  };

  // Build mock result for demo mode
  const store = getGlobalDataStore();
  const allMockPromises = store.getAllPromises();
  let filtered = allMockPromises.filter(x => x.customerId === customerId);

  if (queryParams.status && queryParams.status !== 'all') {
    filtered = filtered.filter(x => x.status === queryParams.status);
  }

  if (queryParams.search) {
    const query = queryParams.search.toLowerCase();
    filtered = filtered.filter(x =>
      x.customerName.toLowerCase().includes(query) ||
      x.customerAccountNumber.toLowerCase().includes(query) ||
      x.promisedAmount.toString().includes(query) ||
      x.id.toLowerCase().includes(query),
    );
  }

  // Apply sorting for mock mode
  if (queryParams.sortBy) {
    const sortKey = queryParams.sortBy;
    const sortDir = queryParams.sortOrder === 'asc' ? 1 : -1;
    filtered = [...filtered].sort((a, b) => {
      let aVal: string | number | Date | undefined;
      let bVal: string | number | Date | undefined;
      switch (sortKey) {
        case 'PromiseId': aVal = a.id; bVal = b.id; break;
        case 'CustomerName': aVal = a.customerName; bVal = b.customerName; break;
        case 'CaseNumber': aVal = a.caseNumber; bVal = b.caseNumber; break;
        case 'PromiseDate': aVal = a.promiseDate.getTime(); bVal = b.promiseDate.getTime(); break;
        case 'DaysOverdue': aVal = a.daysOverdue ?? 0; bVal = b.daysOverdue ?? 0; break;
        case 'PromisedAmount': aVal = a.promisedAmount; bVal = b.promisedAmount; break;
        case 'InvoiceCount': aVal = a.invoices.length; bVal = b.invoices.length; break;
        case 'Status': aVal = a.status; bVal = b.status; break;
        case 'ConfirmationMethod': aVal = a.confirmationMethod; bVal = b.confirmationMethod; break;
        case 'CustomerAccountNumber': aVal = a.customerAccountNumber; bVal = b.customerAccountNumber; break;
        case 'ContactEmail': aVal = a.customerEmail; bVal = b.customerEmail; break;
        case 'ContactPhone': aVal = a.customerPhone; bVal = b.customerPhone; break;
        case 'CreatedAt': aVal = a.createdAt.getTime(); bVal = b.createdAt.getTime(); break;
        case 'CreatedByName': aVal = a.createdBy; bVal = b.createdBy; break;
        case 'Notes': aVal = a.notes; bVal = b.notes; break;
        default: aVal = a.promiseDate.getTime(); bVal = b.promiseDate.getTime(); break;
      }
      if (aVal === undefined) return sortDir;
      if (bVal === undefined) return -sortDir;
      if (aVal < bVal) return -sortDir;
      if (aVal > bVal) return sortDir;
      return 0;
    });
  }

  const mockPage = queryParams.page ?? 1;
  const mockPageSize = queryParams.pageSize ?? 20;
  const totalItems = filtered.length;
  const totalPages = mockPageSize > 0 ? Math.ceil(totalItems / mockPageSize) : 1;
  const mockPaged = filtered.slice((mockPage - 1) * mockPageSize, mockPage * mockPageSize);

  const mockResult: CustomerPromisesResult = {
    promises: mockPaged,
    pagination: {
      page: mockPage,
      pageSize: mockPageSize,
      totalItems,
      totalPages,
      hasNextPage: mockPage < totalPages,
      hasPreviousPage: mockPage > 1,
    },
  };

  const result = useModeAwareQuery<CustomerPromisesResult, Error>(
    ['customer-promises', customerId, queryParams],
    async ({ signal }) => {
      if (!customerId) {
        return { promises: [], pagination: defaultPagination };
      }

      const response = await apiGet<GetCustomerPromisesApiResponse>(
        `/api/v1/promises/customer/${customerId}`,
        queryParams as Record<string, string | number | boolean | undefined>,
        signal,
      );

      const promises = response.data.map(x => mapPromiseFromApi(x));

      return {
        promises,
        pagination: response.pagination,
      };
    },
    mockResult,
    {
      enabled: !!customerId,
      staleTime: 0,
      refetchOnMount: 'always',
    },
  );

  return {
    promises: result.data?.promises ?? [],
    pagination: result.data?.pagination ?? defaultPagination,
    isLoading: result.isLoading,
    error: result.error,
    refetch: result.refetch,
  };
}
